import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Ship,
  Package,
  MapPin,
  MessageSquare,
  CreditCard,
  Truck,
  FileText,
  Bell,
  Search,
  ChevronRight,
  ChevronLeft,
  Container,
  Anchor,
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Menu,
  X,
  Home,
  User,
  Settings,
  LogOut,
  Send,
  Phone,
  Camera,
  Download,
  MapPinned,
  FileImage,
  RefreshCw,
  Mail,
  Train,
  DollarSign,
  Building2,
  Wallet,
  ClipboardList,
  MessageSquarePlus,
  Lightbulb,
  ThumbsUp,
  Building,
  Lock,
  EyeOff,
  Copy,
  Leaf,
  Sparkles,
  ThumbsDown,
  Star,
  Plane,
  GitMerge,
  ShieldCheck,
  AlertTriangle,
} from "lucide-react";
import { IosInstallBanner } from "../components/IosInstallBanner";


// Top Header Bar with Burger Menu
function MobileTopBar({ user, activeTab, setActiveTab }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(["main"]);

  // Grouped menu items
  const menuGroups = [
    {
      id: "main",
      label: "Main",
      items: [
        { id: "home", label: "Home", icon: Home },
        { id: "shipments", label: "My Shipments", icon: Ship },
      ]
    },
    {
      id: "tracking",
      label: "Tracking & Services",
      items: [
        { id: "track", label: "Track Container", icon: Container },
        { id: "quote", label: "Get Quote", icon: DollarSign },
        { id: "my-quotes", label: "My Quotes", icon: ClipboardList },
        { id: "messages", label: "Messages", icon: MessageSquare },
      ]
    },
    {
      id: "portal",
      label: "Customer Portal",
      items: [
        { id: "company", label: "My Company", icon: Building2 },
        { id: "finance", label: "My Finance", icon: Wallet },
        { id: "feedback", label: "Feedback & Suggestions", icon: MessageSquarePlus },
      ]
    },
    {
      id: "account",
      label: "Account",
      items: [
        { id: "profile", label: "My Profile", icon: User },
      ]
    }
  ];

  const tabLabels = {
    home: "Home",
    shipments: "My Shipments",
    track: "Track",
    quote: "Get Quote",
    "my-quotes": "My Quotes",
    messages: "Messages",
    company: "My Company",
    finance: "My Finance",
    feedback: "Feedback",
    profile: "Profile",
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => 
      prev.includes(groupId)
        ? prev.filter(g => g !== groupId)
        : [...prev, groupId]
    );
  };

  return (
    <>
      <header className="sticky top-0 z-40 bg-blue-600 text-white px-4 py-3 flex items-center justify-between shadow-md" data-testid="mobile-top-bar">
        <button 
          onClick={() => setMenuOpen(true)}
          className="p-1.5 -ml-1.5 hover:bg-blue-700 rounded-lg transition-colors"
          data-testid="mobile-burger-menu"
        >
          <Menu className="w-6 h-6" />
        </button>
        <h1 className="font-semibold text-lg">{tabLabels[activeTab] || "myEuropacific"}</h1>
        <div className="w-8" /> {/* Spacer for centering */}
      </header>

      {/* Burger Menu Overlay - uses absolute positioning within the mobile app container */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 z-[100]"
              onClick={() => setMenuOpen(false)}
            />
            {/* Side Menu */}
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="absolute left-0 top-0 bottom-0 w-[85%] max-w-[280px] bg-white z-[101] shadow-xl flex flex-col"
              data-testid="mobile-side-menu"
            >
              {/* Menu Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-5 text-white">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-blue-100">myEuropacific</span>
                  <button 
                    onClick={() => setMenuOpen(false)}
                    className="p-1 hover:bg-blue-700 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold">{user?.full_name || "Customer"}</p>
                    <p className="text-sm text-blue-100">{user?.company_name || "Company"}</p>
                  </div>
                </div>
              </div>

              {/* Menu Items - Collapsible Groups */}
              <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                {menuGroups.map((group) => (
                  <div key={group.id} className="mb-2">
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50 rounded-lg transition-colors"
                      data-testid={`menu-group-${group.id}`}
                    >
                      <span>{group.label}</span>
                      <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${expandedGroups.includes(group.id) ? "rotate-90" : ""}`} />
                    </button>
                    
                    {/* Group Items */}
                    <AnimatePresence initial={false}>
                      {expandedGroups.includes(group.id) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="pl-2 space-y-1 mt-1">
                            {group.items.map((item) => (
                              <button
                                key={item.id}
                                onClick={() => {
                                  setActiveTab(item.id);
                                  setMenuOpen(false);
                                }}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                                  activeTab === item.id
                                    ? "bg-blue-50 text-blue-600"
                                    : "text-slate-700 hover:bg-slate-50"
                                }`}
                                data-testid={`menu-item-${item.id}`}
                              >
                                <item.icon className="w-5 h-5" />
                                <span className="font-medium">{item.label}</span>
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </nav>

              {/* Footer */}
              <div className="p-4 border-t bg-slate-50">
                <p className="text-xs text-slate-400 text-center">
                  Customer Mobile App
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

// Bottom Navigation Bar for Mobile
function MobileNavBar({ activeTab, setActiveTab, badges = {} }) {
  const tabs = [
    { id: "home", label: "Home", icon: Home },
    { id: "shipments", label: "Shipments", icon: Ship },
    { id: "track", label: "Track", icon: Search },
    { id: "messages", label: "Messages", icon: MessageSquare },
    { id: "profile", label: "Profile", icon: User },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50 pb-safe" data-testid="mobile-nav">
      <div className="flex items-center justify-around py-2">
        {tabs.map((tab) => {
          const badge = badges[tab.id];
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative flex flex-col items-center py-2 px-4 rounded-lg transition-colors ${
                activeTab === tab.id ? "text-blue-600" : "text-slate-500"
              }`}
              data-testid={`nav-${tab.id}`}
            >
              <div className="relative">
                <tab.icon className={`w-6 h-6 ${activeTab === tab.id ? "stroke-2" : ""}`} />
                {badge > 0 && (
                  <span
                    className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                    data-testid={`nav-badge-${tab.id}`}
                  >
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </div>
              <span className="text-xs mt-1 font-medium">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// Quick Action Card
function QuickActionCard({ icon: Icon, title, subtitle, onClick, color = "blue" }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    green: "bg-green-50 text-green-600 border-green-100",
    purple: "bg-purple-50 text-purple-600 border-purple-100",
    orange: "bg-orange-50 text-orange-600 border-orange-100",
  };

  return (
    <button
      onClick={onClick}
      className={`w-full p-4 rounded-xl border ${colors[color]} text-left active:scale-98 transition-transform`}
      data-testid={`quick-action-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className="w-8 h-8 mb-2" />
      <h3 className="font-semibold">{title}</h3>
      <p className="text-xs opacity-70 mt-1">{subtitle}</p>
    </button>
  );
}

// Shipment Card for Mobile
function ShipmentCard({ shipment, onClick }) {
  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-700",
      active: "bg-blue-100 text-blue-700",
      in_transit: "bg-purple-100 text-purple-700",
      delivered: "bg-green-100 text-green-700",
      completed: "bg-green-100 text-green-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  return (
    <motion.button
      onClick={onClick}
      className="w-full bg-white rounded-xl border p-4 text-left shadow-sm active:scale-98 transition-transform"
      whileTap={{ scale: 0.98 }}
      data-testid={`shipment-card-${shipment.pf_number || shipment.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-bold text-blue-600">{shipment.pf_number}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(shipment.status)}`}>
              {(shipment.status || "pending").replace(/_/g, " ")}
            </span>
          </div>
          <p className="text-sm text-slate-600 mt-1 line-clamp-1">
            {shipment.description || shipment.commodity || "Shipment"}
          </p>
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
            <MapPin className="w-3 h-3" />
            <span>{shipment.pol || "Origin"}</span>
            <ChevronRight className="w-3 h-3" />
            <span>{shipment.pod || "Destination"}</span>
          </div>
          {shipment.group_member && (
            <div className="mt-2">
              <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">
                {shipment.group_member}
              </span>
            </div>
          )}
        </div>
        <ChevronRight className="w-5 h-5 text-slate-400" />
      </div>
    </motion.button>
  );
}

// Shipment Detail View
function ShipmentDetailView({ shipment, token, onBack }) {
  const [transportOrders, setTransportOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestingPosition, setRequestingPosition] = useState(false);
  const [requestingPod, setRequestingPod] = useState(false);
  const [selectedPod, setSelectedPod] = useState(null);
  const [loadingPod, setLoadingPod] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState("info");
  // 2026-05-15: full PF detail with documents, comments, customs_info, milestones
  const [fullDetail, setFullDetail] = useState(null);
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const [showBLForm, setShowBLForm] = useState(false);
  // 2026-05-16: CO2 footprint + tracking alerts
  const [co2, setCo2] = useState(null);
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [myAlerts, setMyAlerts] = useState([]);

  useEffect(() => {
    fetchTransportOrders();
    fetchMessages();
    fetchFullDetail();
    fetchCo2();
    fetchMyAlerts();
  }, [shipment]);

  const fetchCo2 = async () => {
    if (!shipment?.id) return;
    try {
      const r = await axios.get(`${API_URL}/position-folders/${shipment.id}/co2`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCo2(r.data);
    } catch (e) {
      console.error("CO2 fetch failed", e);
    }
  };

  const fetchMyAlerts = async () => {
    try {
      const r = await axios.get(`${API_URL}/tracking-alerts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyAlerts((r.data?.alerts || []).filter(a => a.pf_id === shipment?.id));
    } catch {
      setMyAlerts([]);
    }
  };

  const fetchFullDetail = async () => {
    if (!shipment?.id) return;
    try {
      const response = await axios.get(
        `${API_URL}/customer-portal/shipment/${shipment.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFullDetail(response.data);
    } catch (error) {
      console.error("Failed to fetch full shipment detail", error);
    }
  };

  const downloadDocument = async (doc) => {
    setDownloadingDocId(doc.id);
    try {
      const response = await axios.get(
        `${API_URL}/customer-portal/shipment/${shipment.id}/documents/${doc.id}/download`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: doc.content_type || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.filename || "document";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${doc.filename}`);
    } catch (error) {
      toast.error("Download failed: " + (error.response?.data?.detail || "Unknown error"));
    } finally {
      setDownloadingDocId(null);
    }
  };

  const fetchTransportOrders = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/customer-portal/shipment/${shipment.id}/transport-orders`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTransportOrders(response.data.transport_orders || []);
    } catch (error) {
      console.error("Failed to fetch transport orders", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    setLoadingMessages(true);
    try {
      const response = await axios.get(
        `${API_URL}/customer-portal/messages/${shipment.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error("Failed to fetch messages", error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim()) return;
    
    setSendingMessage(true);
    try {
      await axios.post(
        `${API_URL}/customer-portal/messages/${shipment.id}`,
        { message: messageText, message_type: "inquiry" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Message sent! Our team will respond shortly.");
      setMessageText("");
      setShowMessageModal(false);
      fetchMessages();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const requestTruckPosition = async () => {
    setRequestingPosition(true);
    try {
      const response = await axios.post(
        `${API_URL}/customer-portal/shipment/${shipment.id}/request-truck-position`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      if (response.data.current_position) {
        toast.info(`Current: ${response.data.current_position.last_known_location || 'In transit'}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request position");
    } finally {
      setRequestingPosition(false);
    }
  };

  const requestPod = async () => {
    setRequestingPod(true);
    try {
      const response = await axios.post(
        `${API_URL}/customer-portal/shipment/${shipment.id}/request-pod`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.pod_available) {
        toast.success("POD is available! Loading...");
        viewPod(response.data.order_id);
      } else {
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request POD");
    } finally {
      setRequestingPod(false);
    }
  };

  const viewPod = async (orderId) => {
    setLoadingPod(true);
    try {
      const response = await axios.get(
        `${API_URL}/customer-portal/transport-order/${orderId}/pod`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedPod(response.data);
    } catch (error) {
      toast.error("POD not available yet");
    } finally {
      setLoadingPod(false);
    }
  };

  const downloadPod = () => {
    if (!selectedPod) return;
    const link = document.createElement('a');
    link.href = `data:${selectedPod.content_type};base64,${selectedPod.data}`;
    link.download = selectedPod.filename || 'POD.pdf';
    link.click();
    toast.success("POD downloaded!");
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-500",
      confirmed: "bg-blue-500",
      loading: "bg-blue-600",
      in_transit: "bg-purple-500",
      delivered: "bg-green-500",
      completed: "bg-green-600",
      pod_received: "bg-green-600"
    };
    return colors[status] || "bg-gray-500";
  };

  // POD Viewer Modal
  if (selectedPod) {
    return (
      <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
        <div className="flex items-center justify-between p-4 text-white">
          <button onClick={() => setSelectedPod(null)} className="p-2">
            <X className="w-6 h-6" />
          </button>
          <span className="font-medium">Proof of Delivery</span>
          <button onClick={downloadPod} className="p-2">
            <Download className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          {selectedPod.content_type.startsWith('image/') ? (
            <img 
              src={`data:${selectedPod.content_type};base64,${selectedPod.data}`}
              alt="POD" 
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          ) : (
            <div className="text-center text-white">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>PDF Document</p>
              <Button onClick={downloadPod} className="mt-4">
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="p-2 -ml-2" data-testid="back-button">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <div>
          <h2 className="text-lg font-bold">{shipment.pf_number}</h2>
          <p className="text-sm text-slate-500">{shipment.customer}</p>
        </div>
      </div>

      {/* Status Card */}
      <div className="bg-white rounded-2xl p-4 border shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-slate-600">Status</span>
          <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(shipment.tracking_status || shipment.status)}`}>
            {(shipment.tracking_status || shipment.status || "pending").replace(/_/g, " ").toUpperCase()}
          </span>
        </div>
        
        {/* Route Info */}
        <div className="flex items-center gap-3 py-3 border-t">
          <div className="flex-1">
            <p className="text-xs text-slate-500">From</p>
            <p className="font-medium">{shipment.pol || shipment.origin_city || "-"}</p>
          </div>
          <Ship className="w-5 h-5 text-blue-500" />
          <div className="flex-1 text-right">
            <p className="text-xs text-slate-500">To</p>
            <p className="font-medium">{shipment.pod || shipment.destination_city || "-"}</p>
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4 py-3 border-t">
          <div>
            <p className="text-xs text-slate-500">ETD</p>
            <p className="font-medium">{shipment.etd?.split('T')[0] || "-"}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">ETA</p>
            <p className="font-medium">{shipment.eta?.split('T')[0] || "-"}</p>
          </div>
        </div>

        {shipment.vessel && (
          <div className="py-3 border-t">
            <p className="text-xs text-slate-500">Vessel</p>
            <p className="font-medium">{shipment.vessel}</p>
          </div>
        )}

        {shipment.container_number && (
          <div className="py-3 border-t">
            <p className="text-xs text-slate-500">Container</p>
            <p className="font-medium font-mono">{shipment.container_number}</p>
          </div>
        )}

        {(shipment.booking_number || shipment.carrier || shipment.shipping_line || shipment.mbl_number || shipment.bl_number) && (
          <div className="grid grid-cols-2 gap-4 py-3 border-t">
            {(shipment.carrier || shipment.shipping_line) && (
              <div>
                <p className="text-xs text-slate-500">Carrier</p>
                <p className="font-medium">{shipment.carrier || shipment.shipping_line}</p>
              </div>
            )}
            {shipment.booking_number && (
              <div>
                <p className="text-xs text-slate-500">Booking No.</p>
                <p className="font-medium font-mono text-xs">{shipment.booking_number}</p>
              </div>
            )}
            {(shipment.mbl_number || shipment.bl_number) && (
              <div>
                <p className="text-xs text-slate-500">B/L No.</p>
                <p className="font-medium font-mono text-xs">{shipment.mbl_number || shipment.bl_number}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <Button 
          onClick={requestTruckPosition}
          disabled={requestingPosition}
          className="h-auto py-4 flex flex-col items-center gap-2"
          variant="outline"
          data-testid="request-truck-position-btn"
        >
          {requestingPosition ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <MapPinned className="w-6 h-6 text-blue-600" />
          )}
          <span className="text-xs">Live Position</span>
        </Button>
        
        <Button 
          onClick={requestPod}
          disabled={requestingPod}
          className="h-auto py-4 flex flex-col items-center gap-2"
          variant="outline"
          data-testid="request-pod-btn"
        >
          {requestingPod ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <FileImage className="w-6 h-6 text-green-600" />
          )}
          <span className="text-xs">Request POD</span>
        </Button>
      </div>

      {/* Transport Orders */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : transportOrders.length > 0 && (
        <div className="bg-white rounded-2xl p-4 border shadow-sm">
          <h3 className="font-semibold mb-3">Deliveries</h3>
          <div className="space-y-3">
            {transportOrders.map((order) => (
              <div key={order.id} className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm">{order.order_number}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(order.workflow_status)} text-white`}>
                    {(order.workflow_status || "pending").replace(/_/g, " ")}
                  </span>
                </div>
                <div className="text-xs text-slate-600 space-y-1">
                  <div className="flex items-center gap-2">
                    <Truck className="w-3 h-3" />
                    <span>{order.trucking_company_name || "Carrier pending"}</span>
                  </div>
                  {order.last_known_location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3 h-3" />
                      <span>{order.last_known_location}</span>
                    </div>
                  )}
                </div>
                {order.has_pod && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="w-full mt-2"
                    onClick={() => viewPod(order.id)}
                    disabled={loadingPod}
                  >
                    {loadingPod ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />}
                    View POD
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* BL Instructions Status */}
      {!shipment.bl_instructions_received && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-orange-800">BL Instructions Pending</h4>
              <p className="text-sm text-orange-700 mt-1 mb-3">
                Please submit your Bill of Lading instructions for this shipment.
              </p>
              <Button
                size="sm"
                onClick={() => setShowBLForm(true)}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="open-bl-instructions-btn"
              >
                <FileText className="w-3 h-3 mr-1.5" />
                Submit BL Instructions
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Customs / TriNet info (MRN / LRN) — merged into milestone area */}
      {fullDetail?.customs_info && (fullDetail.customs_info.mrn || fullDetail.customs_info.lrn || (fullDetail.customs_info.mrn_list || []).length > 0 || (fullDetail.customs_info.lrn_list || []).length > 0) && (
        <div className="bg-white rounded-2xl p-4 border shadow-sm" data-testid="customs-info-block">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-indigo-600" />
            Customs Status
          </h3>
          <div className="space-y-2 text-sm">
            {(fullDetail.customs_info.mrn_list?.length ? fullDetail.customs_info.mrn_list : (fullDetail.customs_info.mrn ? [fullDetail.customs_info.mrn] : [])).map((m, i) => (
              <div key={`mrn-${i}`} className="flex justify-between border-b last:border-b-0 pb-2">
                <span className="text-slate-500">MRN</span>
                <span className="font-mono font-medium text-slate-800">{m}</span>
              </div>
            ))}
            {(fullDetail.customs_info.lrn_list?.length ? fullDetail.customs_info.lrn_list : (fullDetail.customs_info.lrn ? [fullDetail.customs_info.lrn] : [])).map((l, i) => (
              <div key={`lrn-${i}`} className="flex justify-between border-b last:border-b-0 pb-2">
                <span className="text-slate-500">LRN</span>
                <span className="font-mono font-medium text-slate-800">{l}</span>
              </div>
            ))}
            {fullDetail.customs_info.type && (
              <div className="flex justify-between">
                <span className="text-slate-500">Type</span>
                <span className="font-medium">{fullDetail.customs_info.type}</span>
              </div>
            )}
            {fullDetail.customs_info.last_updated_at && (
              <div className="flex justify-between">
                <span className="text-slate-500">Last update</span>
                <span className="text-xs text-slate-600">
                  {new Date(fullDetail.customs_info.last_updated_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CO2 footprint card — 2026-05-16 */}
      <div className="bg-white rounded-2xl p-4 border shadow-sm" data-testid="co2-block">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold flex items-center gap-2">
            <Leaf className="w-4 h-4 text-green-600" />
            Carbon footprint
          </h3>
          {co2 && co2.method === "estimated" && (
            <span className="text-[10px] uppercase tracking-wider text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
              Estimated · GLEC
            </span>
          )}
        </div>
        {!co2 ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : co2.co2_kg == null ? (
          <p className="text-xs text-slate-500">
            {co2.reason || "Insufficient data to estimate emissions."}
          </p>
        ) : (
          <>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-3xl font-bold text-green-700">{co2.co2_kg.toLocaleString()}</span>
              <span className="text-sm text-slate-500">kg CO₂e</span>
              {co2.co2_tonnes != null && (
                <span className="text-xs text-slate-400">≈ {co2.co2_tonnes} t</span>
              )}
            </div>
            <div className="text-xs text-slate-500 grid grid-cols-2 gap-1">
              <span>Mode: <b className="text-slate-700 capitalize">{co2.mode}</b></span>
              <span>Distance: <b className="text-slate-700">{co2.distance_km?.toLocaleString()} km</b></span>
              <span>Weight: <b className="text-slate-700">{co2.weight_tonnes} t</b></span>
              <span>Factor: <b className="text-slate-700">{co2.factor_g_per_tonne_km} g/t·km</b></span>
            </div>
          </>
        )}
      </div>

      {/* Tracking alerts — 2026-05-16 */}
      <div className="bg-white rounded-2xl p-4 border shadow-sm" data-testid="alerts-block">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-600" />
            Tracking alerts ({myAlerts.length})
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowAlertsModal(true)}
            data-testid="manage-alerts-btn"
          >
            {myAlerts.length ? "Manage" : "Set up"}
          </Button>
        </div>
        {myAlerts.length > 0 && (
          <div className="mt-3 space-y-1.5 text-xs">
            {myAlerts.map(a => (
              <div key={a.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-2.5 py-1.5">
                <span className="text-slate-700">
                  {a.triggers.join(", ")}
                </span>
                <span className="text-slate-500">
                  {a.channels.map(c => c === "email" ? "✉" : "💬").join(" ")}
                </span>
              </div>
            ))}
          </div>
        )}
        {myAlerts.length === 0 && (
          <p className="text-xs text-slate-500 mt-2">
            Get pinged on ETA changes, milestones, 2-day arrival heads-up, or delivery — via email and/or WhatsApp.
          </p>
        )}
      </div>

      {/* Tracking alerts modal */}
      {showAlertsModal && (
        <TrackingAlertsModal
          shipment={shipment}
          token={token}
          existing={myAlerts}
          onClose={() => setShowAlertsModal(false)}
          onChanged={fetchMyAlerts}
        />
      )}

      {/* Documents */}
      {fullDetail?.documents && fullDetail.documents.length > 0 && (
        <div className="bg-white rounded-2xl p-4 border shadow-sm" data-testid="documents-block">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" />
            Documents ({fullDetail.documents.length})
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {fullDetail.documents.map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg" data-testid={`document-row-${doc.id}`}>
                <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  {(doc.content_type || "").startsWith("image/") ? (
                    <FileImage className="w-4 h-4 text-blue-700" />
                  ) : (
                    <FileText className="w-4 h-4 text-blue-700" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{doc.filename}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    {doc.tag && <span className="px-1.5 py-0.5 bg-slate-200 rounded text-slate-700">{doc.tag}</span>}
                    {doc.uploaded_at && <span>{new Date(doc.uploaded_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>}
                  </div>
                </div>
                <button
                  onClick={() => downloadDocument(doc)}
                  disabled={downloadingDocId === doc.id}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  data-testid={`download-doc-${doc.id}`}
                >
                  {downloadingDocId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Customer-facing Comments */}
      {fullDetail?.comments && fullDetail.comments.length > 0 && (
        <div className="bg-white rounded-2xl p-4 border shadow-sm" data-testid="comments-block">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-emerald-600" />
            Notes from your forwarder
          </h3>
          <div className="space-y-3 max-h-56 overflow-y-auto">
            {fullDetail.comments.map((c) => (
              <div key={c.id} className="border-l-2 border-emerald-300 pl-3 py-1">
                <p className="text-sm text-slate-700">{c.text}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {c.author_name && <span>{c.author_name} · </span>}
                  {c.created_at && new Date(c.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status Updates Timeline */}
      {shipment.status_updates && shipment.status_updates.length > 0 && (
        <div className="bg-white rounded-2xl p-4 border shadow-sm">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-600" />
            Status Updates
          </h3>
          <div className="space-y-3 max-h-48 overflow-y-auto">
            {[...shipment.status_updates].reverse().map((update, idx) => (
              <div key={idx} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className={`w-2.5 h-2.5 rounded-full ${idx === 0 ? 'bg-blue-500' : 'bg-slate-300'}`} />
                  {idx < shipment.status_updates.length - 1 && (
                    <div className="w-0.5 flex-1 bg-slate-200 my-1" />
                  )}
                </div>
                <div className="flex-1 pb-3">
                  <p className="text-sm font-medium text-slate-800">{update.status}</p>
                  <p className="text-xs text-slate-500">
                    {new Date(update.timestamp).toLocaleString('en-GB', {
                      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                    })}
                    {update.source && ` • ${update.source}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages Section */}
      <div className="bg-white rounded-2xl p-4 border shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-purple-600" />
            Messages
          </h3>
          <Button 
            size="sm" 
            onClick={() => setShowMessageModal(true)}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="send-message-btn"
          >
            <Send className="w-3 h-3 mr-1" />
            Send
          </Button>
        </div>
        
        {loadingMessages ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : messages.length > 0 ? (
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {messages.map((msg, idx) => (
              <div 
                key={idx}
                className={`p-2.5 rounded-lg text-sm ${
                  msg.sender === 'customer' 
                    ? 'bg-purple-50 text-purple-900 ml-4' 
                    : 'bg-slate-100 text-slate-800 mr-4'
                }`}
              >
                <p>{msg.message}</p>
                <p className="text-xs opacity-60 mt-1">
                  {new Date(msg.created_at).toLocaleString('en-GB', {
                    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-4">
            No messages yet. Send a message to contact our team.
          </p>
        )}
      </div>

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
          <div className="bg-white w-full rounded-t-2xl p-4 pb-8 animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Send Message</h3>
              <button onClick={() => setShowMessageModal(false)} className="p-2">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-3">
              Send a message about {shipment.pf_number} to our team
            </p>
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Type your message here... (e.g., 'Please update me on customs status' or 'Need delivery rescheduled')"
              className="w-full p-3 border rounded-lg text-sm resize-none"
              rows={4}
              data-testid="message-textarea"
            />
            <div className="flex gap-2 mt-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowMessageModal(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={sendMessage}
                disabled={sendingMessage || !messageText.trim()}
                data-testid="submit-message-btn"
              >
                {sendingMessage ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Message
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* BL Instructions Form Modal */}
      {showBLForm && (
        <BLInstructionsModal
          pfId={shipment.id}
          pfNumber={shipment.pf_number}
          token={token}
          onClose={() => setShowBLForm(false)}
          onSubmitted={() => {
            setShowBLForm(false);
            fetchFullDetail();
            toast.success("BL Instructions submitted!");
          }}
        />
      )}
    </div>
  );
}

// BL Instructions Form Modal (mobile-optimised) — 2026-05-15
function BLInstructionsModal({ pfId, pfNumber, token, onClose, onSubmitted }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    bl_type: "hbl",
    shipper_name: "",
    shipper_address: "",
    shipper_city: "",
    shipper_country: "",
    shipper_contact: "",
    shipper_phone: "",
    shipper_email: "",
    consignee_name: "",
    consignee_address: "",
    consignee_city: "",
    consignee_country: "",
    consignee_contact: "",
    consignee_phone: "",
    consignee_email: "",
    notify_same_as_consignee: true,
    notify_name: "",
    notify_address: "",
    notify_city: "",
    notify_country: "",
    notify_contact: "",
    notify_phone: "",
    vessel_name: "",
    voyage_number: "",
    port_of_loading: "",
    port_of_discharge: "",
    place_of_receipt: "",
    place_of_delivery: "",
    freight_terms: "prepaid",
    incoterm: "",
    cargo_lines: [],
    declared_value: "",
    special_instructions: "",
  });

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.shipper_name || !form.consignee_name) {
      toast.error("Shipper and consignee names are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/customer-portal/shipment/${pfId}/bl-instructions`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSubmitted();
    } catch (e) {
      toast.error("Submission failed: " + (e.response?.data?.detail || "Unknown error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end" data-testid="bl-instructions-modal">
      <div className="bg-white w-full max-h-[92vh] rounded-t-2xl flex flex-col">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white rounded-t-2xl">
          <div>
            <h3 className="font-semibold">BL Instructions</h3>
            <p className="text-xs text-slate-500">{pfNumber}</p>
          </div>
          <button onClick={onClose} className="p-2" data-testid="bl-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* BL Type */}
          <div>
            <label className="text-sm font-medium text-slate-600 mb-2 block">BL Type</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "hbl", label: "House BL only" },
                { id: "mbl_and_hbl", label: "Master + House BL" },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => update("bl_type", t.id)}
                  className={`p-3 rounded-lg border text-sm font-medium ${form.bl_type === t.id ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200"}`}
                  data-testid={`bl-type-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Shipper */}
          <div className="space-y-2">
            <h4 className="font-medium text-slate-700">Shipper</h4>
            <Input placeholder="Shipper name *" value={form.shipper_name} onChange={(e) => update("shipper_name", e.target.value)} data-testid="bl-shipper-name" />
            <Input placeholder="Address" value={form.shipper_address} onChange={(e) => update("shipper_address", e.target.value)} />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="City" value={form.shipper_city} onChange={(e) => update("shipper_city", e.target.value)} />
              <Input placeholder="Country" value={form.shipper_country} onChange={(e) => update("shipper_country", e.target.value)} />
            </div>
            <Input placeholder="Email" value={form.shipper_email} onChange={(e) => update("shipper_email", e.target.value)} />
          </div>

          {/* Consignee */}
          <div className="space-y-2">
            <h4 className="font-medium text-slate-700">Consignee</h4>
            <Input placeholder="Consignee name *" value={form.consignee_name} onChange={(e) => update("consignee_name", e.target.value)} data-testid="bl-consignee-name" />
            <Input placeholder="Address" value={form.consignee_address} onChange={(e) => update("consignee_address", e.target.value)} />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="City" value={form.consignee_city} onChange={(e) => update("consignee_city", e.target.value)} />
              <Input placeholder="Country" value={form.consignee_country} onChange={(e) => update("consignee_country", e.target.value)} />
            </div>
            <Input placeholder="Email" value={form.consignee_email} onChange={(e) => update("consignee_email", e.target.value)} />
          </div>

          {/* Notify */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" checked={form.notify_same_as_consignee} onChange={(e) => update("notify_same_as_consignee", e.target.checked)} className="rounded" />
              Notify party = Consignee
            </label>
            {!form.notify_same_as_consignee && (
              <>
                <Input placeholder="Notify name" value={form.notify_name} onChange={(e) => update("notify_name", e.target.value)} />
                <Input placeholder="Address" value={form.notify_address} onChange={(e) => update("notify_address", e.target.value)} />
              </>
            )}
          </div>

          {/* Shipment */}
          <div className="space-y-2">
            <h4 className="font-medium text-slate-700">Shipment</h4>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="POL" value={form.port_of_loading} onChange={(e) => update("port_of_loading", e.target.value)} />
              <Input placeholder="POD" value={form.port_of_discharge} onChange={(e) => update("port_of_discharge", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Vessel" value={form.vessel_name} onChange={(e) => update("vessel_name", e.target.value)} />
              <Input placeholder="Voyage" value={form.voyage_number} onChange={(e) => update("voyage_number", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select className="border rounded-md p-2 text-sm" value={form.freight_terms} onChange={(e) => update("freight_terms", e.target.value)}>
                <option value="prepaid">Freight prepaid</option>
                <option value="collect">Freight collect</option>
              </select>
              <Input placeholder="Incoterm (e.g. FOB)" value={form.incoterm} onChange={(e) => update("incoterm", e.target.value)} />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Special instructions</label>
            <textarea
              value={form.special_instructions}
              onChange={(e) => update("special_instructions", e.target.value)}
              rows={3}
              className="w-full p-2 border rounded-md text-sm"
              placeholder="Any extra notes for the BL..."
              data-testid="bl-special-instructions"
            />
          </div>
        </div>
        <div className="p-4 border-t bg-white">
          <Button onClick={submit} disabled={submitting} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="bl-form-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Submit BL Instructions
          </Button>
        </div>
      </div>
    </div>
  );
}

// Home Tab Content
function HomeTab({ user, shipments, setActiveTab, setSelectedShipment, groupInfo }) {
  const pendingBL = shipments.filter(s => !s.bl_instructions_received).length;
  const activeShipments = shipments.filter(s => s.status !== "completed" && s.status !== "delivered").length;
  // 2026-05-16: total CO2 footprint widget
  const [co2Total, setCo2Total] = useState(null);
  const token = localStorage.getItem("token");
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/customer-portal/my-co2-total`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCo2Total(r.data);
      } catch { setCo2Total(null); }
    })();
  }, [token, shipments.length]);

  return (
    <div className="space-y-6 pb-24">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-5 text-white">
        <p className="text-blue-100 text-sm">Welcome back</p>
        <h1 className="text-xl font-bold mt-1">{user?.company_name || user?.full_name}</h1>
        {groupInfo && (
          <div className="mt-2 flex items-center gap-2">
            <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">
              {groupInfo.name} ({groupInfo.member_count} companies)
            </span>
          </div>
        )}
        <div className="flex gap-4 mt-4">
          <div className="flex-1 bg-white/20 rounded-lg p-3">
            <p className="text-2xl font-bold">{activeShipments}</p>
            <p className="text-xs text-blue-100">Active Shipments</p>
          </div>
          <div className="flex-1 bg-white/20 rounded-lg p-3">
            <p className="text-2xl font-bold">{pendingBL}</p>
            <p className="text-xs text-blue-100">Pending BL</p>
          </div>
        </div>
      </div>

      {/* CO2 footprint summary — 2026-05-16 */}
      {co2Total && co2Total.total_co2_kg > 0 && (
        <div className="bg-white rounded-2xl p-4 border shadow-sm" data-testid="co2-total-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold flex items-center gap-2">
              <Leaf className="w-4 h-4 text-green-600" />
              Your carbon footprint
            </h3>
            <span className="text-[10px] uppercase tracking-wider text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
              GLEC · est.
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-green-700">
              {co2Total.total_co2_tonnes ? co2Total.total_co2_tonnes.toFixed(1) : "0"}
            </span>
            <span className="text-sm text-slate-500">t CO₂e total</span>
            <span className="text-xs text-slate-400 ml-auto">
              {co2Total.pf_counted} of {co2Total.pf_count} shipments
            </span>
          </div>
          {Object.keys(co2Total.by_mode_kg || {}).length > 0 && (
            <div className="mt-3 flex gap-2 text-xs">
              {Object.entries(co2Total.by_mode_kg).map(([mode, kg]) => (
                <div key={mode} className="flex-1 bg-slate-50 rounded-lg p-2 text-center">
                  <p className="text-slate-500 capitalize">{mode}</p>
                  <p className="font-semibold text-slate-800">{(kg / 1000).toFixed(2)} t</p>
                </div>
              ))}
            </div>
          )}
          {co2Total.pf_incomplete > 0 && (
            <p className="text-[11px] text-slate-400 mt-2">
              {co2Total.pf_incomplete} shipment{co2Total.pf_incomplete === 1 ? "" : "s"} skipped (missing origin/destination/weight).
            </p>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 gap-3">
          <QuickActionCard
            icon={Container}
            title="Track Container"
            subtitle="Real-time tracking"
            onClick={() => setActiveTab("track")}
            color="blue"
          />
          <QuickActionCard
            icon={MessageSquare}
            title="Messages"
            subtitle="Contact support"
            onClick={() => setActiveTab("messages")}
            color="purple"
          />
          <QuickActionCard
            icon={Ship}
            title="My Shipments"
            subtitle="View all shipments"
            onClick={() => setActiveTab("shipments")}
            color="green"
          />
          <QuickActionCard
            icon={User}
            title="My Profile"
            subtitle="Account settings"
            onClick={() => setActiveTab("profile")}
            color="orange"
          />
        </div>
      </div>

      {/* Recent Shipments */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Recent Shipments</h2>
          <button onClick={() => setActiveTab("shipments")} className="text-sm text-blue-600 font-medium" data-testid="view-all-shipments">View All</button>
        </div>
        <div className="space-y-3">
          {shipments.slice(0, 3).map((shipment) => (
            <ShipmentCard
              key={shipment.id}
              shipment={shipment}
              onClick={() => setSelectedShipment(shipment)}
            />
          ))}
          {shipments.length === 0 && (
            <div className="text-center py-8 text-slate-400">
              <Ship className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No shipments yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Track Tab Content
function TrackTab({ token }) {
  const [query, setQuery] = useState("");
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trackType, setTrackType] = useState("container");

  const handleTrack = async () => {
    if (!query.trim()) {
      toast.error("Please enter a tracking number");
      return;
    }
    
    setLoading(true);
    try {
      let endpoint;
      
      if (trackType === "container") {
        endpoint = `${API_URL}/tracking/container/${query}`;
      } else if (trackType === "bl") {
        endpoint = `${API_URL}/tracking/bl/${query}`;
      } else if (trackType === "metrans") {
        // For Metrans, we need to start a tracking job
        endpoint = `${API_URL}/metrans-tracking/track`;
        const response = await axios.post(endpoint, 
          { container_number: query },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setTracking({
          container_number: query,
          status: response.data.status || "Tracking started",
          source: "Metrans Rail",
          job_id: response.data.job_id,
          needs_otp: response.data.needs_otp,
          message: response.data.needs_otp 
            ? "OTP required - check your email and submit OTP in Metrans Tracking page" 
            : "Tracking in progress"
        });
        if (response.data.needs_otp) {
          toast.info("OTP required - check email and go to Metrans Tracking page to submit");
        } else {
          toast.success("Metrans tracking started!");
        }
        setLoading(false);
        return;
      } else if (trackType === "port-koper") {
        endpoint = `${API_URL}/tracking/port/SIKOP/${query}`;
      } else if (trackType === "port-agct") {
        endpoint = `${API_URL}/tracking/port/HRAGCT/${query}`;
      } else if (trackType === "port-ict") {
        endpoint = `${API_URL}/tracking/port/HRICT/${query}`;
      } else {
        endpoint = `${API_URL}/tracking/container/${query}`;
      }
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTracking(response.data);
      toast.success("Tracking info found!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Tracking not found");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 pb-24">
      {/* Search Box */}
      <div className="bg-white rounded-2xl p-4 border shadow-sm">
        <h2 className="text-lg font-semibold mb-3">Track Your Shipment</h2>
        
        {/* Track Type Tabs - Row 1 */}
        <div className="flex gap-2 mb-2">
          {[
            { id: "container", label: "Container", icon: Container },
            { id: "bl", label: "B/L", icon: FileText },
            { id: "metrans", label: "Metrans", icon: Train },
          ].map((type) => (
            <button
              key={type.id}
              onClick={() => setTrackType(type.id)}
              className={`flex-1 flex items-center justify-center gap-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                trackType === type.id
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
              data-testid={`track-type-${type.id}`}
            >
              <type.icon className="w-4 h-4" />
              {type.label}
            </button>
          ))}
        </div>
        
        {/* Track Type Tabs - Row 2 (Ports) */}
        <div className="flex gap-2 mb-4">
          {[
            { id: "port-koper", label: "Koper", icon: Anchor },
            { id: "port-agct", label: "Rijeka AGCT", icon: Anchor },
            { id: "port-ict", label: "Rijeka ICT", icon: Anchor },
          ].map((type) => (
            <button
              key={type.id}
              onClick={() => setTrackType(type.id)}
              className={`flex-1 flex items-center justify-center gap-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                trackType === type.id
                  ? "bg-emerald-600 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
              data-testid={`track-type-${type.id}`}
            >
              <type.icon className="w-4 h-4" />
              {type.label}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value.toUpperCase())}
            placeholder={
              trackType === "container" ? "MRKU1234567" :
              trackType === "bl" ? "BL Number" :
              trackType === "metrans" ? "Container (e.g., DFSU 204941-0)" :
              trackType === "port-koper" ? "Container for Koper" :
              trackType === "port-agct" ? "Container for Rijeka AGCT" :
              trackType === "port-ict" ? "Container for Rijeka ICT" :
              "Container Number"
            }
            className="flex-1"
            onKeyDown={(e) => e.key === "Enter" && handleTrack()}
            data-testid="track-input"
          />
          <Button onClick={handleTrack} disabled={loading} data-testid="track-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Tracking Results */}
      {tracking && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-4 border shadow-sm"
          data-testid="tracking-results"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Container className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold">{tracking.container_number || tracking.bl_number || query}</h3>
              <p className="text-sm text-slate-500">{tracking.carrier || tracking.shipping_line || "-"}</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-500">Status</span>
              <span className="font-medium">{tracking.status || tracking.tracking_status || "Unknown"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Location</span>
              <span className="font-medium">{tracking.last_location || tracking.current_location || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">ETA</span>
              <span className="font-medium">{tracking.eta || "-"}</span>
            </div>
            {tracking.vessel && (
              <div className="flex justify-between">
                <span className="text-slate-500">Vessel</span>
                <span className="font-medium">{tracking.vessel}</span>
              </div>
            )}
          </div>

          <Button className="w-full mt-4" variant="outline" data-testid="add-to-watchlist">
            <Bell className="w-4 h-4 mr-2" />
            Add to Watchlist
          </Button>
        </motion.div>
      )}
    </div>
  );
}

// Messages Tab Content
function MessagesTab({ token, shipments }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  // 2026-05-16: Roket AI in-app chat session id (per device)
  const [aiSessionId, setAiSessionId] = useState(() =>
    localStorage.getItem("roket_ai_session_id") || ""
  );

  useEffect(() => {
    fetchConversations();
  }, []);

  // The hard-pinned "Roket AI" conversation always sits on top
  const ROKET_CONVO = {
    id: "roket-ai",
    pf_number: "Roket AI",
    last_message: "Ask anything about your shipments, ETA, docs, CO₂ …",
    timestamp: new Date().toISOString(),
    unread: false,
    is_ai: true,
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/customer-portal/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConversations([ROKET_CONVO, ...(response.data.conversations || [])]);
    } catch (error) {
      // Fallback: shipments as convos + Roket pinned
      const convos = shipments.slice(0, 10).map(s => ({
        id: s.id,
        pf_number: s.pf_number,
        last_message: "Tap to start conversation",
        timestamp: s.created_at,
        unread: false,
      }));
      setConversations([ROKET_CONVO, ...convos]);
    }
  };

  const fetchMessages = async (convo) => {
    setLoadingMessages(true);
    try {
      // AI conversation: pull history of the current session if any
      if (convo.is_ai) {
        if (aiSessionId) {
          try {
            const r = await axios.get(
              `${API_URL}/customer-portal/ai-chat/${aiSessionId}/history`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            const turns = (r.data?.messages || []).map((m, i) => ({
              id: `${aiSessionId}-${i}`,
              sender: m.role === "assistant" ? "europacific" : "customer",
              message: m.text,
              created_at: m.at,
            }));
            // Greet on empty
            setMessages(turns.length ? turns : [{
              id: "ai-welcome",
              sender: "europacific",
              message: "Hi! I'm Roket, your EuroPacific assistant. Ask me about any of your shipments, ETAs, BL instructions, documents, or your CO₂ footprint.",
              created_at: new Date().toISOString(),
            }]);
          } catch {
            setMessages([{
              id: "ai-welcome",
              sender: "europacific",
              message: "Hi! I'm Roket. Ask me anything about your shipments.",
              created_at: new Date().toISOString(),
            }]);
          }
        } else {
          setMessages([{
            id: "ai-welcome",
            sender: "europacific",
            message: "Hi! I'm Roket, your EuroPacific assistant. Ask me anything about your shipments, ETAs, BL instructions, documents, or your CO₂ footprint.",
            created_at: new Date().toISOString(),
          }]);
        }
        setLoadingMessages(false);
        return;
      }
      // Regular human conversation
      const response = await axios.get(`${API_URL}/customer-portal/messages/${convo.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      setMessages([
        {
          id: "welcome",
          sender: "europacific",
          message: `Hello! How can we help you with shipment ${convo.pf_number}?`,
          created_at: new Date().toISOString(),
        }
      ]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const selectConversation = (convo) => {
    setSelectedConvo(convo);
    fetchMessages(convo);
  };

  const sendMessage = async () => {
    if (!message.trim() || !selectedConvo) return;
    setSending(true);
    const userText = message.trim();
    const userTurn = {
      id: Date.now().toString(),
      sender: "customer",
      message: userText,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userTurn]);
    setMessage("");
    try {
      // ── AI chat branch ──
      if (selectedConvo.is_ai) {
        const r = await axios.post(
          `${API_URL}/customer-portal/ai-chat`,
          { message: userText, session_id: aiSessionId || undefined },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (r.data?.session_id && r.data.session_id !== aiSessionId) {
          setAiSessionId(r.data.session_id);
          localStorage.setItem("roket_ai_session_id", r.data.session_id);
        }
        setMessages((prev) => [...prev, {
          id: userTurn.id + "-ai",
          sender: "europacific",
          message: r.data?.reply || "(no reply)",
          created_at: new Date().toISOString(),
        }]);
        return;
      }
      // ── Human conversation branch (existing behaviour) ──
      await axios.post(
        `${API_URL}/customer-portal/messages/${selectedConvo.id}`,
        { message: userText, message_type: "general" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages((prev) => [...prev, {
        id: userTurn.id + "-auto",
        sender: "europacific",
        message: "Thank you for your message. We are checking and will revert shortly.",
        created_at: new Date().toISOString(),
      }]);
    } catch (e) {
      setMessages((prev) => [...prev, {
        id: userTurn.id + "-err",
        sender: "europacific",
        message: "Sorry, I couldn't reach the server. Please try again.",
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
    }
  };

  if (selectedConvo) {
    return (
      <div className="flex flex-col h-[calc(100vh-180px)]">
        {/* Chat Header */}
        <div className="bg-white border-b p-4 flex items-center gap-3">
          <button onClick={() => setSelectedConvo(null)} className="p-2" data-testid="back-to-conversations">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h3 className="font-semibold flex items-center gap-1.5">
              {selectedConvo.is_ai && <Sparkles className="w-4 h-4 text-purple-500" />}
              {selectedConvo.pf_number}
            </h3>
            <p className="text-xs text-slate-500">
              {selectedConvo.is_ai ? "AI assistant · powered by Roket" : "Europacific Support"}
            </p>
          </div>
          <button className="p-2">
            <Phone className="w-5 h-5 text-blue-600" />
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50 space-y-3">
          {loadingMessages ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`max-w-[80%] p-3 rounded-2xl ${
                  msg.sender === "customer"
                    ? "bg-blue-600 text-white ml-auto rounded-br-sm"
                    : "bg-white shadow-sm rounded-tl-sm"
                }`}
              >
                <p className="text-sm">{msg.message}</p>
                <p className={`text-xs mt-1 ${msg.sender === "customer" ? "text-blue-200" : "text-slate-400"}`}>
                  {msg.sender === "customer" ? "You" : "Europacific"}
                </p>
              </div>
            ))
          )}
        </div>

        {/* Message Input */}
        <div className="bg-white border-t p-4 pb-safe">
          <div className="flex gap-2">
            <Input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1"
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              data-testid="message-input"
            />
            <Button onClick={sendMessage} disabled={sending} data-testid="send-message">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      <h2 className="text-lg font-semibold">Messages</h2>
      
      {conversations.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No conversations yet</p>
          <p className="text-sm mt-1">Start by selecting a shipment</p>
        </div>
      ) : (
        <div className="space-y-2">
          {conversations.map((convo) => (
            <button
              key={convo.id}
              onClick={() => selectConversation(convo)}
              className={`w-full rounded-xl border p-4 text-left flex items-center gap-3 active:scale-98 transition-transform ${convo.is_ai ? "bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200" : "bg-white"}`}
              data-testid={`conversation-${convo.pf_number}`}
            >
              <div className={`h-12 w-12 rounded-full flex items-center justify-center ${convo.is_ai ? "bg-purple-100" : "bg-blue-100"}`}>
                {convo.is_ai
                  ? <Sparkles className="w-6 h-6 text-purple-600" />
                  : <Ship className="w-6 h-6 text-blue-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-1">
                    {convo.pf_number}
                    {convo.is_ai && <span className="text-[9px] bg-purple-600 text-white rounded px-1 ml-1">AI</span>}
                  </h3>
                  {convo.unread && <div className="w-2 h-2 bg-blue-600 rounded-full" />}
                </div>
                <p className="text-sm text-slate-500 truncate">{convo.last_message}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Profile Tab Content
function ProfileTab({ user, onLogout, token }) {
  const [linkedCompanies, setLinkedCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/customer-portal/my-companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLinkedCompanies(r.data.companies || []);
      } catch (e) {
        // Quietly ignore — old users may not have any linkage yet
        setLinkedCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    })();
  }, [token]);

  return (
    <div className="space-y-6 pb-24">
      {/* Profile Header */}
      <div className="bg-white rounded-2xl p-5 border text-center">
        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
          <User className="w-10 h-10 text-blue-600" />
        </div>
        <h2 className="text-xl font-bold mt-3">{user?.full_name || "User"}</h2>
        <p className="text-slate-500">{user?.company_name}</p>
        <p className="text-sm text-slate-400">{user?.email}</p>
      </div>

      {/* Account Info */}
      <div className="bg-white rounded-2xl border overflow-hidden">
        <div className="p-4 border-b">
          <div className="flex items-center gap-3">
            <Ship className="w-5 h-5 text-slate-500" />
            <div>
              <span className="text-sm text-slate-500">Company</span>
              <p className="font-medium">{user?.company_name || "Not set"}</p>
            </div>
          </div>
        </div>
        <div className="p-4 border-b">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-slate-500" />
            <div>
              <span className="text-sm text-slate-500">Email</span>
              <p className="font-medium">{user?.email || "Not set"}</p>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-slate-500" />
            <div>
              <span className="text-sm text-slate-500">Phone</span>
              <p className="font-medium">{user?.phone || "Not set"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Linked Companies — 2026-05-16: multi-branch support */}
      <div className="bg-white rounded-2xl border overflow-hidden" data-testid="profile-linked-companies">
        <div className="p-4 border-b bg-slate-50">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <Building className="w-4 h-4 text-blue-600" />
            Companies linked to your profile
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            You can see shipments &amp; documents for every company below.
          </p>
        </div>
        {loadingCompanies ? (
          <div className="p-4 text-center text-sm text-slate-400">Loading…</div>
        ) : linkedCompanies.length === 0 ? (
          <div className="p-4 text-center text-sm text-slate-500">
            <p>No companies linked yet.</p>
            <p className="text-xs text-slate-400 mt-1">
              Contact EuroPacific support to link your account to your CRM company.
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {linkedCompanies.map((c, idx) => (
              <div key={c.id || idx} className="p-4 flex items-center gap-3" data-testid={`profile-linked-company-${idx}`}>
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{c.name || c.id}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {[c.city, c.country].filter(Boolean).join(", ") || "—"}
                    {c.email && ` • ${c.email}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PUK Code (Vault) — 2026-05-16: visible recovery code */}
      <ProfilePukVault token={token} />

      {/* Logout */}
      <Button
        variant="outline"
        className="w-full text-red-600 border-red-200 hover:bg-red-50"
        onClick={onLogout}
        data-testid="logout-button"
      >
        <LogOut className="w-4 h-4 mr-2" />
        Sign Out
      </Button>
    </div>
  );
}

// PUK Vault — lets the customer (re-)issue their PUK on demand. The
// stored PUK is hashed so the original can't be retrieved; clicking
// "Generate new" invalidates the previous one and shows the new code
// exactly once.
function ProfilePukVault({ token }) {
  const [revealed, setRevealed] = useState(false);
  const [puk, setPuk] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const reissue = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/customer-portal/my-puk/reissue`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPuk(r.data.puk_code || null);
      setRevealed(true);
      setConfirmOpen(false);
      toast.success("New PUK generated. Save it now — you won't see it again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not re-issue PUK");
    } finally {
      setLoading(false);
    }
  };
  const copy = async () => {
    if (!puk) return;
    try {
      await navigator.clipboard.writeText(puk);
      toast.success("PUK copied to clipboard");
    } catch { toast.error("Couldn't copy"); }
  };
  return (
    <div className="bg-white rounded-2xl border overflow-hidden" data-testid="profile-puk-vault">
      <div className="p-4 border-b bg-amber-50">
        <h3 className="font-semibold text-sm flex items-center gap-2 text-amber-900">
          <Lock className="w-4 h-4 text-amber-600" />
          PUK (recovery code)
        </h3>
        <p className="text-xs text-amber-700 mt-0.5">
          Used to reset your password if you forget it. We store it hashed —
          you can re-issue a fresh code anytime and it will be shown <b>once</b>.
        </p>
      </div>
      <div className="p-4">
        {!revealed ? (
          !confirmOpen ? (
            <Button
              onClick={() => setConfirmOpen(true)}
              variant="outline"
              className="w-full"
              data-testid="open-puk-confirm"
            >
              <Lock className="w-4 h-4 mr-2" />
              Generate new PUK
            </Button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-slate-600">
                Generating a new PUK will <b>invalidate your previous one</b>.
                Continue?
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm" onClick={() => setConfirmOpen(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={reissue}
                  disabled={loading}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="confirm-puk-reissue"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
                </Button>
              </div>
            </div>
          )
        ) : (
          <div className="space-y-2">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center font-mono text-lg tracking-widest text-slate-800" data-testid="puk-value">
              {puk || "—"}
            </div>
            <p className="text-xs text-amber-700">
              ⚠️ Save this in your password manager. You won't see it again.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" onClick={copy} data-testid="copy-puk-btn">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setRevealed(false); setPuk(null); }}>
                <EyeOff className="w-3.5 h-3.5 mr-1.5" /> Hide
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Quote Request Tab Content (Mobile)
// 2026-05-16 — overhauled to match the europacific.com/quote wizard:
// 5 service types (FCL/LCL/Air/Inland/Multimodal), full cargo + additional
// services + incoterm + door pickup/delivery + hazardous + insurance.
function QuoteRequestTab({ token, user }) {
  const [submitting, setSubmitting] = useState(false);
  const [lastQuoteNumber, setLastQuoteNumber] = useState(null);
  const [formData, setFormData] = useState({
    // Service
    service_type: "",
    // Route
    origin_country: "",
    origin_city: "",
    origin_address: "",
    origin_port_airport: "",
    destination_country: "",
    destination_city: "",
    destination_address: "",
    destination_port_airport: "",
    // FCL specific
    container_type: "",
    container_quantity: 1,
    // Cargo
    commodity: "",
    total_weight_kg: "",
    total_volume_cbm: "",
    package_count: "",
    package_type: "Carton",
    is_hazardous: false,
    un_number: "",
    hs_code: "",
    // Schedule & terms
    cargo_ready_date: "",
    incoterm: "",
    // Additional services
    pre_carriage: false,
    on_carriage: false,
    customs_clearance: false,
    customs_type: "",
    insurance: false,
    insurance_value: "",
    warehousing: false,
    packaging: false,
    fumigation: false,
    // Notes
    special_requirements: "",
  });

  const serviceTypes = [
    { id: "fcl", name: "FCL", icon: Ship, desc: "Full container" },
    { id: "lcl", name: "LCL", icon: Package, desc: "Less than container" },
    { id: "air", name: "Air", icon: Plane, desc: "Air freight" },
    { id: "inland", name: "Inland", icon: Truck, desc: "Road / Rail" },
    { id: "multimodal", name: "Multimodal", icon: GitMerge, desc: "Combined" },
  ];

  const containerTypes = ["20'GP", "40'GP", "40'HC", "45'HC", "20'RF", "40'RF", "20'OT", "40'OT", "20'FR", "40'FR"];
  const incoterms = ["EXW", "FCA", "FOB", "CFR", "CIF", "CPT", "CIP", "DAP", "DPU", "DDP"];
  const packageTypes = ["Carton", "Pallet", "Crate", "Drum", "Bag", "Bundle", "Roll", "Loose"];

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.service_type) {
      toast.error("Please pick a service type");
      return;
    }
    if (!formData.origin_city || !formData.destination_city) {
      toast.error("Origin and destination city are required");
      return;
    }
    if (!formData.origin_country || !formData.destination_country) {
      toast.error("Please enter origin & destination country codes (e.g. CN, SI)");
      return;
    }
    setSubmitting(true);
    try {
      const cargo_items = (formData.commodity || formData.is_hazardous || formData.hs_code)
        ? [{
            description: formData.commodity || "",
            package_type: formData.package_type || "Carton",
            quantity: formData.package_count ? Number(formData.package_count) : 1,
            weight_kg: formData.total_weight_kg ? Number(formData.total_weight_kg) : 0,
            volume_cbm: formData.total_volume_cbm ? Number(formData.total_volume_cbm) : null,
            is_hazardous: !!formData.is_hazardous,
            un_number: formData.un_number || null,
            hs_code: formData.hs_code || null,
          }]
        : [];

      const additional_services = {
        pre_carriage: !!formData.pre_carriage,
        pre_carriage_address: formData.pre_carriage ? (formData.origin_address || null) : null,
        on_carriage: !!formData.on_carriage,
        on_carriage_address: formData.on_carriage ? (formData.destination_address || null) : null,
        customs_clearance: !!formData.customs_clearance,
        customs_type: formData.customs_clearance ? (formData.customs_type || null) : null,
        insurance: !!formData.insurance,
        insurance_value: formData.insurance && formData.insurance_value ? Number(formData.insurance_value) : null,
        insurance_currency: "EUR",
        warehousing: !!formData.warehousing,
        packaging: !!formData.packaging,
        fumigation: !!formData.fumigation,
      };

      const payload = {
        service_type: formData.service_type,
        origin_country: formData.origin_country,
        origin_city: formData.origin_city,
        origin_address: formData.origin_address || null,
        origin_port_airport: formData.origin_port_airport || null,
        destination_country: formData.destination_country,
        destination_city: formData.destination_city,
        destination_address: formData.destination_address || null,
        destination_port_airport: formData.destination_port_airport || null,
        container_type: formData.service_type === "fcl" ? (formData.container_type || null) : null,
        container_quantity: formData.service_type === "fcl" ? Number(formData.container_quantity || 1) : 1,
        cargo_items,
        total_weight_kg: formData.total_weight_kg ? Number(formData.total_weight_kg) : null,
        total_volume_cbm: formData.total_volume_cbm ? Number(formData.total_volume_cbm) : null,
        commodity: formData.commodity || null,
        cargo_ready_date: formData.cargo_ready_date || null,
        additional_services,
        incoterm: formData.incoterm || null,
        company_name: user?.company_name || "",
        contact_name: user?.full_name || "",
        email: user?.email || "",
        phone: user?.phone || "",
        special_requirements: formData.special_requirements || null,
      };

      const r = await axios.post(
        `${API_URL}/customer-portal/quote-request`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const qno = r.data?.quote_number;
      setLastQuoteNumber(qno);
      toast.success(`Quote request submitted! Ref ${qno || ""}`.trim());
      setFormData({
        service_type: "",
        origin_country: "", origin_city: "", origin_address: "", origin_port_airport: "",
        destination_country: "", destination_city: "", destination_address: "", destination_port_airport: "",
        container_type: "", container_quantity: 1,
        commodity: "", total_weight_kg: "", total_volume_cbm: "",
        package_count: "", package_type: "Carton",
        is_hazardous: false, un_number: "", hs_code: "",
        cargo_ready_date: "", incoterm: "",
        pre_carriage: false, on_carriage: false,
        customs_clearance: false, customs_type: "",
        insurance: false, insurance_value: "",
        warehousing: false, packaging: false, fumigation: false,
        special_requirements: "",
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  // Reusable checkbox row
  const Check = ({ field, label, icon: Icon }) => (
    <button
      type="button"
      onClick={() => updateField(field, !formData[field])}
      className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${
        formData[field] ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white"
      }`}
      data-testid={`quote-toggle-${field}`}
    >
      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
        formData[field] ? "bg-blue-600 border-blue-600" : "border-slate-300"
      }`}>
        {formData[field] && <CheckCircle className="w-4 h-4 text-white" />}
      </div>
      {Icon && <Icon className="w-4 h-4 text-slate-500" />}
      <span className="text-sm font-medium text-slate-700">{label}</span>
    </button>
  );

  const svc = formData.service_type;

  return (
    <div className="space-y-4 pb-24">
      <h2 className="text-lg font-semibold">Request a Quote</h2>

      {lastQuoteNumber && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3" data-testid="quote-success-banner">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-green-800">Quote received — ref {lastQuoteNumber}</p>
            <p className="text-xs text-green-700">Our sales team will reply by email shortly. You can also check "My Quotes".</p>
          </div>
        </div>
      )}

      {/* Section 1 — Service Type */}
      <div className="bg-white rounded-xl border p-4 space-y-3">
        <label className="text-sm font-semibold text-slate-700 block">1. Service Type</label>
        <div className="grid grid-cols-3 gap-2">
          {serviceTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => updateField("service_type", type.id)}
              className={`p-3 rounded-xl border text-center transition-all ${
                formData.service_type === type.id
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-slate-200"
              }`}
              data-testid={`quote-service-${type.id}`}
            >
              <type.icon className="w-5 h-5 mx-auto mb-1" />
              <span className="text-sm font-medium block">{type.name}</span>
              <span className="text-[10px] text-slate-400 leading-tight block">{type.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Section 2 — Route */}
      <div className="bg-white rounded-xl border p-4 space-y-3">
        <label className="text-sm font-semibold text-slate-700 block">2. Route</label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Origin city *</label>
            <Input placeholder="e.g. Shanghai" value={formData.origin_city}
              onChange={(e) => updateField("origin_city", e.target.value)} />
            <Input placeholder="Country (CN, DE…)" value={formData.origin_country}
              onChange={(e) => updateField("origin_country", e.target.value.toUpperCase())}
              className="mt-1.5 text-xs" data-testid="origin-country-input" />
            <Input placeholder={svc === "air" ? "Origin airport (PVG…)" : "Origin port (CNSHA…)"}
              value={formData.origin_port_airport}
              onChange={(e) => updateField("origin_port_airport", e.target.value)}
              className="mt-1.5 text-xs" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Destination city *</label>
            <Input placeholder="e.g. Koper" value={formData.destination_city}
              onChange={(e) => updateField("destination_city", e.target.value)} />
            <Input placeholder="Country (SI, IT…)" value={formData.destination_country}
              onChange={(e) => updateField("destination_country", e.target.value.toUpperCase())}
              className="mt-1.5 text-xs" data-testid="destination-country-input" />
            <Input placeholder={svc === "air" ? "Dest. airport (LJU…)" : "Dest. port (SIKOP…)"}
              value={formData.destination_port_airport}
              onChange={(e) => updateField("destination_port_airport", e.target.value)}
              className="mt-1.5 text-xs" />
          </div>
        </div>
        {/* Door addresses appear only if pre/on-carriage requested */}
        {formData.pre_carriage && (
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Pickup address (door)</label>
            <Input placeholder="Street, city, postcode"
              value={formData.origin_address}
              onChange={(e) => updateField("origin_address", e.target.value)}
              data-testid="origin-address-input" />
          </div>
        )}
        {formData.on_carriage && (
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Delivery address (door)</label>
            <Input placeholder="Street, city, postcode"
              value={formData.destination_address}
              onChange={(e) => updateField("destination_address", e.target.value)}
              data-testid="destination-address-input" />
          </div>
        )}
      </div>

      {/* Section 3 — Cargo */}
      <div className="bg-white rounded-xl border p-4 space-y-3">
        <label className="text-sm font-semibold text-slate-700 block">3. Cargo</label>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Commodity *</label>
          <Input placeholder="What are you shipping?" value={formData.commodity}
            onChange={(e) => updateField("commodity", e.target.value)} />
        </div>

        {/* FCL specifics */}
        {svc === "fcl" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Container type</label>
              <select
                value={formData.container_type}
                onChange={(e) => updateField("container_type", e.target.value)}
                className="w-full p-2 border border-slate-200 rounded-md text-sm bg-white"
                data-testid="quote-container-type"
              >
                <option value="">Select…</option>
                {containerTypes.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Quantity</label>
              <Input type="number" min="1" placeholder="1"
                value={formData.container_quantity}
                onChange={(e) => updateField("container_quantity", e.target.value)} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Weight (kg) *</label>
            <Input type="number" placeholder="e.g. 1200" value={formData.total_weight_kg}
              onChange={(e) => updateField("total_weight_kg", e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Volume (CBM)</label>
            <Input type="number" step="0.01" placeholder="e.g. 4.5" value={formData.total_volume_cbm}
              onChange={(e) => updateField("total_volume_cbm", e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Pieces</label>
            <Input type="number" placeholder="# of packages"
              value={formData.package_count}
              onChange={(e) => updateField("package_count", e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Package type</label>
            <select
              value={formData.package_type}
              onChange={(e) => updateField("package_type", e.target.value)}
              className="w-full p-2 border border-slate-200 rounded-md text-sm bg-white"
              data-testid="quote-package-type"
            >
              {packageTypes.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">HS Code (optional)</label>
          <Input placeholder="e.g. 8471.30" value={formData.hs_code}
            onChange={(e) => updateField("hs_code", e.target.value)} />
        </div>

        {/* Hazardous toggle */}
        <Check field="is_hazardous" label="Hazardous / DG cargo" icon={AlertTriangle} />
        {formData.is_hazardous && (
          <div>
            <label className="text-xs text-slate-500 mb-1 block">UN number</label>
            <Input placeholder="e.g. UN1950" value={formData.un_number}
              onChange={(e) => updateField("un_number", e.target.value)}
              data-testid="quote-un-number" />
          </div>
        )}
      </div>

      {/* Section 4 — Schedule & Terms */}
      <div className="bg-white rounded-xl border p-4 space-y-3">
        <label className="text-sm font-semibold text-slate-700 block">4. Schedule &amp; Terms</label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Cargo ready date</label>
            <Input type="date" value={formData.cargo_ready_date}
              onChange={(e) => updateField("cargo_ready_date", e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Incoterm 2020</label>
            <select
              value={formData.incoterm}
              onChange={(e) => updateField("incoterm", e.target.value)}
              className="w-full p-2 border border-slate-200 rounded-md text-sm bg-white"
              data-testid="quote-incoterm"
            >
              <option value="">Select…</option>
              {incoterms.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Section 5 — Additional Services */}
      <div className="bg-white rounded-xl border p-4 space-y-2">
        <label className="text-sm font-semibold text-slate-700 block mb-1">5. Additional Services</label>
        <Check field="pre_carriage" label="Pickup at origin (door)" icon={Truck} />
        <Check field="on_carriage" label="Delivery to destination (door)" icon={Truck} />
        <Check field="customs_clearance" label="Customs clearance" icon={FileText} />
        {formData.customs_clearance && (
          <div className="ml-2">
            <select
              value={formData.customs_type}
              onChange={(e) => updateField("customs_type", e.target.value)}
              className="w-full p-2 border border-slate-200 rounded-md text-sm bg-white"
              data-testid="quote-customs-type"
            >
              <option value="">Select customs type…</option>
              <option value="import">Import</option>
              <option value="export">Export</option>
              <option value="transit">Transit</option>
            </select>
          </div>
        )}
        <Check field="insurance" label="Cargo insurance" icon={ShieldCheck} />
        {formData.insurance && (
          <div className="ml-2">
            <Input type="number" placeholder="Declared value (EUR)"
              value={formData.insurance_value}
              onChange={(e) => updateField("insurance_value", e.target.value)}
              data-testid="quote-insurance-value" />
          </div>
        )}
        <Check field="warehousing" label="Warehousing" icon={Package} />
        <Check field="packaging" label="Packaging / re-packing" icon={Package} />
        <Check field="fumigation" label="Fumigation" icon={Leaf} />
      </div>

      {/* Section 6 — Notes */}
      <div className="bg-white rounded-xl border p-4 space-y-2">
        <label className="text-sm font-semibold text-slate-700 block">6. Special Requirements</label>
        <textarea
          className="w-full p-3 border border-slate-200 rounded-md text-sm min-h-[80px]"
          placeholder="Temperature-controlled, oversized cargo, weekend delivery, anything else our sales team should know…"
          value={formData.special_requirements}
          onChange={(e) => updateField("special_requirements", e.target.value)}
          data-testid="quote-special-requirements"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full bg-blue-600 h-12"
        data-testid="quote-submit-btn"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
        Submit Request
      </Button>
    </div>
  );
}

// My Quotes Tab Content (Mobile)
function MyQuotesTab({ token }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuotes();
  }, [token]);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/customer-portal/my-quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data.quotes || []);
    } catch (error) {
      try {
        const response = await axios.get(`${API_URL}/quotes/my-quotes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setQuotes(response.data.quotes || []);
      } catch (e) {
        setQuotes([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-700",
      quoted: "bg-green-100 text-green-700",
      accepted: "bg-emerald-100 text-emerald-700",
      declined: "bg-red-100 text-red-700",
    };
    return colors[status] || "bg-gray-100 text-gray-600";
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      <h2 className="text-lg font-semibold">My Quotes</h2>
      
      {quotes.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border">
          <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No quotes yet</p>
          <p className="text-sm text-slate-400">Request a quote to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {quotes.map((quote) => (
            <div key={quote.id} className="bg-white rounded-xl border p-4">
              <div className="flex items-start justify-between">
                <div>
                  <span className="font-bold text-blue-600">{quote.quote_number || quote.id?.slice(0,8)}</span>
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${getStatusColor(quote.status)}`}>
                    {quote.status}
                  </span>
                </div>
              </div>
              <p className="text-sm text-slate-600 mt-1">{quote.commodity || "Cargo"}</p>
              <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                <MapPin className="w-3 h-3" />
                <span>{quote.origin_city || quote.origin}</span>
                <ChevronRight className="w-3 h-3" />
                <span>{quote.destination_city || quote.destination}</span>
              </div>
              {quote.quoted_price && (
                <div className="mt-3 p-2 bg-green-50 rounded-lg">
                  <span className="font-semibold text-green-700">
                    {quote.currency || "EUR"} {quote.quoted_price.toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// My Company Tab Content (Mobile)
function MyCompanyTab({ token, user }) {
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompanyData();
  }, [token]);

  const fetchCompanyData = async () => {
    try {
      const response = await axios.get(`${API_URL}/customer-portal/my-company`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyData(response.data);
    } catch (error) {
      setCompanyData(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      <h2 className="text-lg font-semibold">My Company</h2>
      
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <Building2 className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold">{companyData?.company?.name || user?.company_name || "Your Company"}</h3>
            <p className="text-sm text-slate-500">{companyData?.company?.country || "Slovenia"}</p>
          </div>
        </div>

        <div className="space-y-3 pt-3 border-t">
          {companyData?.company?.address && (
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm text-slate-500">Address</p>
                <p className="text-sm">{companyData.company.address}</p>
              </div>
            </div>
          )}
          {companyData?.company?.vat_number && (
            <div className="flex items-start gap-2">
              <FileText className="w-4 h-4 text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm text-slate-500">VAT Number</p>
                <p className="text-sm">{companyData.company.vat_number}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contacts */}
      {companyData?.contacts && companyData.contacts.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="p-3 bg-slate-50 border-b">
            <h3 className="font-medium text-sm">Contacts</h3>
          </div>
          {companyData.contacts.map((contact, idx) => (
            <div key={idx} className="p-3 border-b last:border-b-0">
              <p className="font-medium">{contact.name}</p>
              <p className="text-sm text-slate-500">{contact.email}</p>
              {contact.phone && <p className="text-sm text-slate-500">{contact.phone}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// My Finance Tab Content (Mobile)
function MyFinanceTab({ token }) {
  const [invoices, setInvoices] = useState([]);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvoices();
  }, [token]);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API_URL}/customer-portal/my-invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.invoices || []);
      setTotalOutstanding(response.data.total_outstanding || 0);
    } catch (error) {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      <h2 className="text-lg font-semibold">My Finance</h2>
      
      {/* Balance Summary */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-4 text-white">
        <p className="text-blue-100 text-sm">Outstanding Balance</p>
        <p className="text-2xl font-bold mt-1">EUR {totalOutstanding.toLocaleString()}</p>
      </div>

      {/* Recent Invoices */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="p-3 bg-slate-50 border-b">
          <h3 className="font-medium text-sm">Recent Invoices</h3>
        </div>
        {invoices.length === 0 ? (
          <div className="p-4 text-center text-slate-400">
            <Wallet className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No invoices found</p>
          </div>
        ) : (
          invoices.slice(0, 5).map((invoice) => (
            <div key={invoice.id} className="p-3 border-b last:border-b-0 flex items-center justify-between">
              <div>
                <p className="font-medium">{invoice.invoice_number}</p>
                <p className="text-xs text-slate-500">
                  {invoice.date ? new Date(invoice.date).toLocaleDateString() : "-"}
                </p>
              </div>
              <div className="text-right">
                <p className="font-semibold">{invoice.currency || "EUR"} {invoice.amount?.toLocaleString()}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  invoice.status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                }`}>
                  {invoice.status}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Feedback Tab Content (Mobile)
function FeedbackTab({ token, user }) {
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const categories = [
    { id: "general", label: "General", icon: MessageSquare },
    { id: "feature", label: "Feature Request", icon: Lightbulb },
    { id: "service", label: "Service", icon: Star },
  ];

  const handleSubmit = async () => {
    if (!category || !message.trim()) {
      toast.error("Please select a category and enter your feedback");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/customer-portal/feedback`, {
        category,
        message,
        rating,
        customer_name: user?.full_name,
        customer_email: user?.email,
        customer_company: user?.company_name,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Thank you for your feedback!");
      setSubmitted(true);
      setCategory("");
      setMessage("");
      setRating(null);
    } catch (error) {
      toast.error("Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="space-y-4 pb-24">
        <div className="bg-white rounded-xl border p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Thank You!</h2>
          <p className="text-slate-500 mt-2">Your feedback helps us improve our services.</p>
          <Button className="mt-6" onClick={() => setSubmitted(false)}>
            <MessageSquarePlus className="w-4 h-4 mr-2" />
            Submit Another
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      <h2 className="text-lg font-semibold">Feedback & Suggestions</h2>
      <p className="text-sm text-slate-500">Help us serve you better</p>
      
      <div className="bg-white rounded-xl border p-4 space-y-4">
        {/* Category Selection */}
        <div>
          <label className="text-sm font-medium text-slate-600 mb-2 block">Category</label>
          <div className="grid grid-cols-3 gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setCategory(cat.id)}
                className={`p-3 rounded-xl border text-center transition-all ${
                  category === cat.id
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-slate-200"
                }`}
              >
                <cat.icon className="w-5 h-5 mx-auto mb-1" />
                <span className="text-xs font-medium">{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="text-sm font-medium text-slate-600 mb-1 block">Your Feedback</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Share your thoughts, suggestions, or feature ideas..."
            rows={4}
            className="w-full px-3 py-2 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Rating */}
        {(category === "general" || category === "service") && (
          <div>
            <label className="text-sm font-medium text-slate-600 mb-2 block">Rate our service</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((val) => (
                <button
                  key={val}
                  onClick={() => setRating(val)}
                  className={`flex-1 p-2 rounded-lg border transition-all ${
                    rating === val ? "border-amber-400 bg-amber-50" : "border-slate-200"
                  }`}
                >
                  <Star className={`w-5 h-5 mx-auto ${rating >= val ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
                </button>
              ))}
            </div>
          </div>
        )}

        <Button
          onClick={handleSubmit}
          disabled={submitting || !category || !message.trim()}
          className="w-full bg-blue-600"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
          Submit Feedback
        </Button>
      </div>

      {/* Encouragement */}
      <div className="p-4 bg-blue-50 rounded-xl space-y-2">
        <p className="text-sm text-blue-700">
          <strong>Your voice matters!</strong> Feature requests are reviewed monthly and prioritized based on customer demand.
        </p>
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-xs text-amber-800 font-medium flex items-center gap-1">
            <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
            <span>Reward for great ideas!</span>
          </p>
          <p className="text-xs text-amber-700 mt-1">
            If your suggestion is implemented, we'll issue you a <strong>€100 voucher</strong> for future shipments!
          </p>
        </div>
      </div>
    </div>
  );
}

// Shipments Tab Content
function ShipmentsTab({ shipments, setSelectedShipment, refreshShipments, loading, linkedCompanies = [] }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // 2026-05-16: branch switcher — "all" or one of the linked Pantheon companies
  const [branchFilter, setBranchFilter] = useState("all");

  const branchMatch = (s, branch) => {
    if (branch === "all") return true;
    const target = (branchOptions.find(b => b.id === branch) || {});
    const name = (target.name || "").toLowerCase();
    const sCust = ((s.customer_name || s.customer || "") + "").toLowerCase();
    return (
      (target.id && (s.linked_company_id === target.id || s.company_id === target.id))
      || (name && sCust.includes(name))
    );
  };

  const branchOptions = [
    { id: "all", name: `All branches (${shipments.length})` },
    ...linkedCompanies.map(c => ({
      id: c.id, name: c.name || c.id,
    })),
  ];

  const filtered = shipments.filter((s) => {
    if (statusFilter === "active" && (s.status === "completed" || s.status === "delivered")) return false;
    if (statusFilter === "completed" && s.status !== "completed" && s.status !== "delivered") return false;
    if (statusFilter === "pending_bl" && s.bl_instructions_received) return false;
    if (!branchMatch(s, branchFilter)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (s.pf_number || "").toLowerCase().includes(q) ||
      (s.description || "").toLowerCase().includes(q) ||
      (s.commodity || "").toLowerCase().includes(q) ||
      (s.container_number || "").toLowerCase().includes(q) ||
      (s.bl_number || "").toLowerCase().includes(q) ||
      (s.pol || "").toLowerCase().includes(q) ||
      (s.pod || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">My Shipments</h2>
        <button onClick={refreshShipments} className="p-2 text-blue-600" data-testid="refresh-shipments">
          <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Branch switcher — only render when the customer is linked to >1 branch */}
      {linkedCompanies.length > 1 && (
        <div data-testid="branch-switcher" className="bg-white border rounded-xl p-2 flex items-center gap-2">
          <Building className="w-4 h-4 text-blue-600 ml-1 flex-shrink-0" />
          <select
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            className="flex-1 bg-transparent text-sm font-medium outline-none"
            data-testid="branch-switcher-select"
          >
            {branchOptions.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search PF / container / BL / route..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="shipments-search"
        />
      </div>

      {/* Quick filter chips */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {[
          { id: "all", label: `All (${shipments.length})` },
          { id: "active", label: `Active (${shipments.filter((s) => s.status !== "completed" && s.status !== "delivered").length})` },
          { id: "pending_bl", label: `Pending BL (${shipments.filter((s) => !s.bl_instructions_received).length})` },
          { id: "completed", label: "Completed" },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setStatusFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${statusFilter === f.id ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`}
            data-testid={`filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Ship className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{search ? "No matching shipments" : "No shipments found"}</p>
          {search && (
            <button onClick={() => setSearch("")} className="text-sm text-blue-600 mt-2">Clear search</button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((shipment) => (
            <ShipmentCard
              key={shipment.id}
              shipment={shipment}
              onClick={() => setSelectedShipment(shipment)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Tracking Alerts Modal — lets the customer subscribe to ETA / milestone /
// 2-day-arrival / delivered alerts via email and/or WhatsApp.
function TrackingAlertsModal({ shipment, token, existing, onClose, onChanged }) {
  const initial = existing && existing[0];
  const [channels, setChannels] = useState(initial?.channels || ["email"]);
  const [triggers, setTriggers] = useState(initial?.triggers || ["eta_change", "arrival_2d"]);
  const [phone, setPhone] = useState(initial?.phone || "");
  const [saving, setSaving] = useState(false);

  const toggle = (set, setter, val) =>
    setter(set.includes(val) ? set.filter(x => x !== val) : [...set, val]);

  const save = async () => {
    if (channels.length === 0) {
      toast.error("Pick at least one channel"); return;
    }
    if (channels.includes("whatsapp") && !phone) {
      toast.error("Add your phone number for WhatsApp"); return;
    }
    setSaving(true);
    try {
      if (initial) {
        await axios.put(`${API_URL}/tracking-alerts/${initial.id}`,
          { channels, triggers, phone, enabled: true },
          { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API_URL}/tracking-alerts`,
          { pf_id: shipment.id, channels, triggers, phone },
          { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success("Tracking alerts saved");
      onChanged && onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save alerts");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!initial) return;
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/tracking-alerts/${initial.id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Alerts removed");
      onChanged && onChanged();
      onClose();
    } catch (e) {
      toast.error("Failed to remove alerts");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50">
      <div className="bg-white w-full rounded-t-2xl p-4 pb-8 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Tracking alerts — {shipment.pf_number}</h3>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <p className="text-xs text-slate-500 mb-3">
          We'll ping you for each event you tick below, on each channel you pick.
        </p>

        <p className="text-xs font-medium text-slate-700 mb-1.5">Send to</p>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {[
            { id: "email",    label: "✉️ Email" },
            { id: "whatsapp", label: "💬 WhatsApp" },
          ].map(c => (
            <button
              key={c.id}
              onClick={() => toggle(channels, setChannels, c.id)}
              className={`py-2 rounded-lg border text-sm ${channels.includes(c.id) ? "bg-blue-50 border-blue-300 text-blue-800 font-medium" : "border-slate-200 text-slate-500"}`}
              data-testid={`alert-channel-${c.id}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {channels.includes("whatsapp") && (
          <div className="mb-4">
            <p className="text-xs font-medium text-slate-700 mb-1.5">WhatsApp number (E.164)</p>
            <Input
              placeholder="+38640123456"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="alert-phone-input"
            />
          </div>
        )}

        <p className="text-xs font-medium text-slate-700 mb-1.5">Notify me when</p>
        <div className="space-y-2 mb-4">
          {[
            { id: "eta_change", label: "ETA changes",                 icon: "📅" },
            { id: "milestone",  label: "New tracking milestone",       icon: "🚢" },
            { id: "arrival_2d", label: "Arriving in ~2 days",          icon: "⏰" },
            { id: "delivered",  label: "Shipment marked delivered",    icon: "✅" },
          ].map(t => (
            <label key={t.id} className="flex items-center gap-3 p-2.5 border rounded-lg cursor-pointer hover:bg-slate-50">
              <input
                type="checkbox"
                checked={triggers.includes(t.id)}
                onChange={() => toggle(triggers, setTriggers, t.id)}
                className="w-4 h-4"
                data-testid={`alert-trigger-${t.id}`}
              />
              <span className="text-sm">{t.icon} {t.label}</span>
            </label>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2">
          {initial && (
            <Button variant="outline" onClick={remove} disabled={saving} className="text-red-600 border-red-200" data-testid="alert-remove-btn">
              Remove
            </Button>
          )}
          <Button
            onClick={save}
            disabled={saving}
            className={`bg-blue-600 hover:bg-blue-700 ${initial ? "" : "col-span-2"}`}
            data-testid="alert-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
            {initial ? "Update" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Main Mobile App Component
export default function MobileAppPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("home");
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [groupInfo, setGroupInfo] = useState(null);
  // 2026-05-16: linked branches for the multi-company switcher in Shipments
  const [linkedCompanies, setLinkedCompanies] = useState([]);

  useEffect(() => {
    fetchShipments();
    // Fetch the user's linked companies for the branch switcher
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/customer-portal/my-companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLinkedCompanies(r.data?.companies || []);
      } catch {
        setLinkedCompanies([]);
      }
    })();
  }, [token]);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/customer-portal/my-shipments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShipments(response.data.shipments || []);
      setGroupInfo(response.data.group || null);
    } catch (error) {
      console.error("Failed to fetch shipments", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading && shipments.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // If viewing shipment detail
  if (selectedShipment) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col relative">
        <MobileTopBar user={user} activeTab={activeTab} setActiveTab={(tab) => { setSelectedShipment(null); setActiveTab(tab); }} />
        <main className="flex-1 px-4 py-4 pb-20 overflow-auto">
          <ShipmentDetailView 
            shipment={selectedShipment} 
            token={token}
            onBack={() => setSelectedShipment(null)}
          />
        </main>
        <MobileNavBar activeTab={activeTab} setActiveTab={(tab) => { setSelectedShipment(null); setActiveTab(tab); }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col relative">
      {/* Top Navigation Bar */}
      <MobileTopBar user={user} activeTab={activeTab} setActiveTab={setActiveTab} />
      
      {/* Main Content */}
      <main className="flex-1 px-4 py-4 pb-20 overflow-auto">
        <AnimatePresence mode="wait">
          {activeTab === "home" && (
            <motion.div
              key="home"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <HomeTab 
                user={user} 
                shipments={shipments} 
                setActiveTab={setActiveTab} 
                setSelectedShipment={setSelectedShipment}
                groupInfo={groupInfo}
              />
            </motion.div>
          )}
          {activeTab === "shipments" && (
            <motion.div
              key="shipments"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <ShipmentsTab 
                shipments={shipments} 
                setSelectedShipment={setSelectedShipment}
                refreshShipments={fetchShipments}
                loading={loading}
                linkedCompanies={linkedCompanies}
              />
            </motion.div>
          )}
          {activeTab === "track" && (
            <motion.div
              key="track"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <TrackTab token={token} />
            </motion.div>
          )}
          {activeTab === "messages" && (
            <motion.div
              key="messages"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <MessagesTab token={token} shipments={shipments} />
            </motion.div>
          )}
          {activeTab === "quote" && (
            <motion.div
              key="quote"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <QuoteRequestTab token={token} user={user} />
            </motion.div>
          )}
          {activeTab === "my-quotes" && (
            <motion.div
              key="my-quotes"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <MyQuotesTab token={token} />
            </motion.div>
          )}
          {activeTab === "company" && (
            <motion.div
              key="company"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <MyCompanyTab token={token} user={user} />
            </motion.div>
          )}
          {activeTab === "finance" && (
            <motion.div
              key="finance"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <MyFinanceTab token={token} />
            </motion.div>
          )}
          {activeTab === "feedback" && (
            <motion.div
              key="feedback"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <FeedbackTab token={token} user={user} />
            </motion.div>
          )}
          {activeTab === "profile" && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <ProfileTab user={user} onLogout={handleLogout} token={token} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Bottom Navigation */}
      <MobileNavBar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        badges={{
          shipments: shipments.filter((s) => s.status !== "completed" && s.status !== "delivered").length,
        }}
      />

      {/* 2026-05-16 — iOS "Add to Home Screen" nudge banner */}
      <IosInstallBanner appName="myEuropacific" accent="blue" storageKey="ios-install-banner-customer" />
    </div>
  );
}
